import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import * as React from 'react';

import { OVERLAY_LAYER } from './types/overlay-environment-variants-enums';
import { OverlayLayerBoundary } from './overlay-environment';
import { useFloatingPortalContainer, useOverlayLayerZIndex } from './overlay-environment-context';

import { cn } from '@/lib/utils';

import { DROPDOWN_MENU_DENSITY, DropdownMenuItemVariant } from './types/dropdown-menu-variants-enums';



const DropdownMenuStyleContext = React.createContext<DROPDOWN_MENU_DENSITY | undefined>(
  undefined
);

interface DropdownMenuProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {
  density?: DROPDOWN_MENU_DENSITY;
}

function DropdownMenu({ density, ...props }: DropdownMenuProps) {
  return (
    <DropdownMenuStyleContext.Provider value={density}>
      <DropdownMenuPrimitive.Root {...props} />
    </DropdownMenuStyleContext.Provider>
  );
}
DropdownMenu.displayName = DropdownMenuPrimitive.Root.displayName;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

// z-dropdown (150) clears z-sidebar (100) so menus always render above the sidebar
const styledDropdownMenuContentClasses =
  'z-dropdown min-w-[180px] max-w-[320px] overflow-hidden rounded-surface border border-border-default bg-surface-raised p-1 text-content-primary shadow-raised data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';
const styledDropdownMenuItemClasses =
  'relative flex cursor-default select-none items-center gap-2 rounded-affordance px-2 type-body text-content-primary outline-none transition-[color,background-color] duration-standard ease-standard focus:bg-neutral-interaction-hover focus:text-content-primary motion-reduce:transition-none data-disabled:pointer-events-none data-disabled:bg-surface-foundation-subtle data-disabled:text-content-disabled data-disabled:opacity-100 pointer-coarse:h-11 [&_svg]:size-4 [&_svg]:shrink-0';

function getDropdownMenuDensityClasses(density: DROPDOWN_MENU_DENSITY | undefined) {
  if (density === DROPDOWN_MENU_DENSITY.COMPACT) {
    return 'h-8 py-0 !type-field-label';
  }
  if (density === DROPDOWN_MENU_DENSITY.DEFAULT) {
    return 'h-10 py-0';
  }
  return undefined;
}

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
    className?: string;
  }
>(({ className, inset, children, ...props }, ref) => {
  const density = React.use(DropdownMenuStyleContext);

  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        density === undefined
          ? 'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-interaction-hover focus:text-interaction-hover-foreground data-[state=open]:bg-interaction-selected data-[state=open]:text-interaction-selected-foreground'
          : styledDropdownMenuItemClasses,
        density !== undefined &&
          'data-[state=open]:bg-neutral-interaction-selected data-[state=open]:text-content-primary',
        getDropdownMenuDensityClasses(density),
        inset && 'pl-8',
        className
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto h-4 w-4" />
    </DropdownMenuPrimitive.SubTrigger>
  );
});
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
    className?: string;
    container?: React.ComponentPropsWithoutRef<
      typeof DropdownMenuPrimitive.Portal
    >['container'];
  }
>(({ className, children, container, style, ...props }, ref) => {
  const density = React.use(DropdownMenuStyleContext);
  const portalContainer = useFloatingPortalContainer(container);
  const zIndex = useOverlayLayerZIndex(OVERLAY_LAYER.DROPDOWN);

  return (
    <DropdownMenuPrimitive.Portal container={portalContainer as HTMLElement | undefined}>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        className={cn(
          density === undefined
            ? 'z-dropdown min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-card-elevated data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
            : styledDropdownMenuContentClasses,
          className
        )}
        style={{ ...style, zIndex }}
        {...props}
      >
        <OverlayLayerBoundary zIndex={zIndex}>{children}</OverlayLayerBoundary>
      </DropdownMenuPrimitive.SubContent>
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {
  className?: string;
  container?: React.ComponentPropsWithoutRef<
    typeof DropdownMenuPrimitive.Portal
  >['container'];
  density?: DROPDOWN_MENU_DENSITY;
  sideOffset?: number;
}

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, children, container, density, sideOffset = 4, style, ...props }, ref) => {
  const rootDensity = React.use(DropdownMenuStyleContext);
  const effectiveDensity = density ?? rootDensity;
  const portalContainer = useFloatingPortalContainer(container);
  const zIndex = useOverlayLayerZIndex(OVERLAY_LAYER.DROPDOWN);

  return (
    <DropdownMenuStyleContext.Provider value={effectiveDensity}>
      <DropdownMenuPrimitive.Portal container={portalContainer as HTMLElement | undefined}>
        <DropdownMenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(
            effectiveDensity === undefined
              ? 'z-dropdown min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-card-elevated data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
              : styledDropdownMenuContentClasses,
            className
          )}
          style={{ ...style, zIndex }}
          {...props}
        >
          <OverlayLayerBoundary zIndex={zIndex}>{children}</OverlayLayerBoundary>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuStyleContext.Provider>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    className?: string;
    variant?: DropdownMenuItemVariant;
  }
>(({ className, inset, variant = DropdownMenuItemVariant.DEFAULT, ...props }, ref) => {
  const density = React.use(DropdownMenuStyleContext);

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      data-variant={variant}
      className={cn(
        density === undefined
          ? 'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-interaction-hover focus:text-interaction-hover-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive data-disabled:pointer-events-none data-disabled:opacity-50'
          : styledDropdownMenuItemClasses,
        density !== undefined &&
          'data-[variant=destructive]:text-status-danger-foreground data-[variant=destructive]:focus:bg-status-danger-surface data-[variant=destructive]:focus:text-status-danger-foreground',
        getDropdownMenuDensityClasses(density),
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  );
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
    className?: string;
    checked?: boolean;
  }
>(({ className, children, checked, ...props }, ref) => {
  const density = React.use(DropdownMenuStyleContext);

  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        density === undefined
          ? 'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-interaction-hover focus:text-interaction-hover-foreground data-disabled:pointer-events-none data-disabled:opacity-50'
          : styledDropdownMenuItemClasses,
        getDropdownMenuDensityClasses(density),
        density !== undefined && 'pl-8 pr-2',
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check
            className={cn('h-4 w-4', density !== undefined && 'text-interaction-selected-indicator')}
          />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & {
    className?: string;
  }
>(({ className, children, ...props }, ref) => {
  const density = React.use(DropdownMenuStyleContext);

  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        density === undefined
          ? 'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-interaction-hover focus:text-interaction-hover-foreground data-disabled:pointer-events-none data-disabled:opacity-50'
          : styledDropdownMenuItemClasses,
        getDropdownMenuDensityClasses(density),
        density !== undefined && 'pl-8 pr-2',
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle
            className={cn(
              'h-2 w-2 fill-current',
              density !== undefined && 'text-interaction-selected-indicator'
            )}
          />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
    className?: string;
  }
>(({ className, inset, ...props }, ref) => {
  const density = React.use(DropdownMenuStyleContext);

  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        density === undefined
          ? 'px-2 py-1.5 text-sm font-semibold'
          : 'flex items-center px-2 type-field-label font-medium text-content-secondary pointer-coarse:h-11',
        getDropdownMenuDensityClasses(density),
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  );
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  className?: string;
}) => {
  return (
    <span className={cn('ml-auto type-settings-row-meta tracking-widest text-content-tertiary', className)} {...props} />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger };
